import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { PRESS_PHOTO_VARIANTS, PROFILE_BANNER_KINDS } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { profileFileUrl } from '../profileAssets';
import { slotsChip, type ProfileChip } from '../profileChips';
import { profileSlotOf, profileSlotsOf, type ProfileSlot } from '../profileSlots';
import { profileMessages as m } from '../profileMessages';
import type { ProfileTool } from '../profileRoute';
import { ProfileChipBadge } from './ProfileChipBadge';
import type { ProfileCardProps } from './profileCardProps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PhoneRow {
  tool: ProfileTool;
  name: MessageDescriptor;
  sub: MessageDescriptor;
  chip: ProfileChip | { tone: 'ok' | 'nil'; text: MessageDescriptor };
  thumbUrl: string | null;
}

function thumbOf(slots: readonly ProfileSlot[]): string | null {
  return slots.map((slot) => profileFileUrl(slot.current)).find((url) => url !== null) ?? null;
}

/** Below 700 px (design d1): one row per kind after the Spotify card; a tap opens its tool. */
const ProfilePhoneRowsComponent: React.FC<{ card: ProfileCardProps }> = ({ card }) => {
  const { profile, openTool } = card;
  const intl = useIntl();
  const rows = useMemo((): PhoneRow[] => {
    const avatar = [profileSlotOf(profile.assets, 'spotifyAvatar')];
    const banners = PROFILE_BANNER_KINDS.map((kind) => profileSlotOf(profile.assets, kind));
    const press = profileSlotsOf(profile.assets, 'pressPhoto', PRESS_PHOTO_VARIANTS);
    const bioEmpty = profile.bio.bioShort === '' && profile.bio.bioLong === '';
    return [
      {
        tool: 'spotifyAvatar',
        name: m.artistPhoto,
        sub: m.spotifySizeNarrow,
        chip: slotsChip(avatar),
        thumbUrl: thumbOf(avatar),
      },
      {
        tool: 'banners',
        name: m.bannersName,
        sub: m.bannersSize,
        chip: slotsChip(banners),
        thumbUrl: thumbOf(banners),
      },
      { tool: 'pressPhoto', name: m.pressName, sub: m.pressSize, chip: slotsChip(press), thumbUrl: thumbOf(press) },
      {
        tool: 'bio',
        name: m.bioName,
        sub: m.bioSizeNarrow,
        chip: { tone: bioEmpty ? 'nil' : 'ok', text: bioEmpty ? m.chipEmpty : m.chipSaved },
        thumbUrl: null,
      },
    ];
  }, [profile.assets, profile.bio]);

  return (
    <div className="d-flex flex-column gap-2" data-testid="profile-phone-rows">
      {rows.map((row) => (
        <button
          key={row.tool}
          type="button"
          className={styles.phoneRow}
          data-testid={`profile-row-${row.tool}`}
          onClick={() => openTool(row.tool)}
        >
          <span
            className={styles.phoneThumb}
            style={row.thumbUrl === null ? undefined : { backgroundImage: `url("${row.thumbUrl}")` }}
          />
          <span className={styles.grow}>
            <b>{intl.formatMessage(row.name)}</b>
            <small>{intl.formatMessage(row.sub)}</small>
          </span>
          <ProfileChipBadge chip={row.chip} />
        </button>
      ))}
    </div>
  );
};

export const ProfilePhoneRows = TypedMemo(ProfilePhoneRowsComponent);
